import { useState, useCallback, useEffect, useContext } from 'react';
import { AuthContext } from '../context/authContext';

// Hook del sistema de notificaciones a usuarios (mensajes creados por admin).
// Maneja el listado, el contador de no leídas y el marcado como leídas.
export const useNotificacionesUsuario = () => {
    const { user, userId, role, isAuthenticated } = useContext(AuthContext);
    const [notificaciones, setNotificaciones] = useState([]);
    const [noLeidas, setNoLeidas] = useState(0);
    const [loading, setLoading] = useState(false);

    const getToken = useCallback(() => {
        try {
            const raw = localStorage.getItem('token');
            return raw && raw !== '' ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }, []);

    const apiRequest = useCallback(async (endpoint, options = {}) => {
        const token = getToken();
        if (!token) return null;
        try {
            const res = await fetch(endpoint, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    ...(options.headers || {})
                },
                ...options
            });
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            return await res.json();
        } catch (error) {
            console.error('[notifUsuario] error en request:', error?.message, endpoint);
            return null;
        }
    }, [getToken]);

    const cargarNotificaciones = useCallback(async (opts = {}) => {
        if (!isAuthenticated || role === 'admin') return; // el admin crea, no recibe
        setLoading(true);
        const params = new URLSearchParams();
        if (opts.limite) params.set('limite', opts.limite);
        if (opts.offset) params.set('offset', opts.offset);
        if (opts.soloNoLeidas) params.set('soloNoLeidas', 'true');

        const res = await apiRequest(`/api/notificaciones-usuario?${params.toString()}`);
        if (res?.success && Array.isArray(res.data)) {
            setNotificaciones(res.data);
        }
        setLoading(false);
    }, [apiRequest, isAuthenticated, role]);

    const cargarContador = useCallback(async () => {
        if (!isAuthenticated || role === 'admin') {
            setNoLeidas(0);
            return;
        }
        const res = await apiRequest('/api/notificaciones-usuario/no-leidas');
        if (res?.success) {
            setNoLeidas(res.data?.noLeidas || 0);
        }
    }, [apiRequest, isAuthenticated, role]);

    const marcarComoLeidas = useCallback(async (ids) => {
        if (!isAuthenticated || role === 'admin') return;
        const body = Array.isArray(ids) && ids.length > 0 ? { ids } : { todas: true };
        const res = await apiRequest('/api/notificaciones-usuario/marcar-leidas', {
            method: 'PATCH',
            body: JSON.stringify(body)
        });
        if (Array.isArray(ids) && ids.length > 0) {
            // Marcar solo las indicadas
            const marcadas = new Set(ids.map(Number));
            setNotificaciones(prev => prev.map(n =>
                marcadas.has(Number(n.notificacion_id)) ? { ...n, leida: true, fecha_lectura: new Date().toISOString() } : n
            ));
            setNoLeidas(prev => Math.max(0, prev - (res?.data?.marcadas ?? 0)));
            return true;
        }
        // Marcar todas: refresca completamente
        setNoLeidas(0);
        return cargarNotificaciones();
    }, [apiRequest, isAuthenticated, role, cargarNotificaciones]);

    // Carga inicial del contador al autenticarse
    useEffect(() => {
        if (isAuthenticated && role && role !== 'admin') {
            cargarNotificaciones({ limite: 50 });
            cargarContador();
        } else {
            setNoLeidas(0);
            setNotificaciones([]);
        }
    }, [isAuthenticated, role, cargarNotificaciones, cargarContador, user, userId]);

    return {
        notificaciones,
        noLeidas,
        loading,
        cargarNotificaciones,
        cargarContador,
        marcarComoLeidas
    };
};